// set only the config options that are different from the defaults from libs/shared/store/config.module
// the configs gets merged in ./store/index.ts
import type { ClientConfig, MapStyle } from "@shared/config/types";
import { RouteName } from "@shared/types/routes";
import { Addons } from "@shared/types/config";
import {
  destinationMarker,
  destinationOperators,
  loungeMarker,
  loungeOperators,
} from "./operatorId";
import type { DeepPartial } from "@chargetrip/frontend-utilities";
import { IconName } from "@discover/components";

const lightMapStyle: DeepPartial<MapStyle> = {
  styleUrl: "mapbox://styles/chargetrip/cmfdq6leu008j01sd1xb25m5m",
  assets: {
    station: {
      cluster: {
        text: {
          offset: [0, -0.4],
        },
      },
      markers: {
        100: {
          index: 100,
          offset: [0, -21],
          power: {
            from: 0,
            to: 43,
          },
          legend: {
            isHidden: true,
          },
          operators: [destinationMarker, loungeMarker],
        },
        200: {
          index: 200,
          offset: [0, -21],
          power: {
            from: 43,
            to: 150,
          },
          legend: {
            isHidden: true,
          },
          operators: [destinationMarker, loungeMarker],
        },
        300: {
          index: 300,
          offset: [0, -21],
          power: {
            from: 150,
            to: Infinity,
          },
          legend: {
            isHidden: false,
          },
          operators: [destinationMarker, loungeMarker],
        },
      },
    },
  },
  layers: {
    routing: {
      alternativeStations: {
        selected: {
          icon: {
            offset: [0, 0],
          },
        },
      },
      polyline: {
        fill: {
          paint: "rgb(64, 149, 247)",
        },
        border: {
          paint: "rgb(25, 81, 237)",
        },
      },
    },
    isoline: {
      polygon: {
        fill: {
          paint: "rgb(64, 149, 247)",
          opacity: 0.25,
        },
        border: {
          paint: "rgb(25, 81, 237)",
        },
      },
    },
  },
};

const config: ClientConfig = {
  application: {
    manifest: {
      title: "Porsche",
      url: "https://porsche-production.discover.chargetrip.com",
    },
    addons: [Addons.SETTINGS, Addons.SEARCH],
    initialization: {
      landingPage: RouteName.PLAN_ROUTE,
      ui: {
        themes: {
          modes: ["light"],
          defaultMode: "light",
        },
        footnotes: {
          isHidden: true,
        },
        onboarding: {
          vehicles: {
            isHidden: false,
          },
        },
        headerLogo: "detached",
        vehicle: {
          details: {
            isHidden: true,
          },
          formatting: {
            make: false,
            model: true,
            version: true,
          },
        },
      },
      localization: {
        language: {
          defaultValue: "en-DE",
          supported: [
            "en-DE",
            "en-IT",
            "de-DE",
            "en-GB",
            "en-US",
            "fr-FR",
            "nl-NL",
            "it-IT",
            "es-ES",
          ],
        },
      },
      tracking: {
        gtmContainerId: "",
        fathomId: "PCTQAMFB",
      },
      map: {
        zoomRegion: new Map([
          ["default", { zoomLevel: 9, position: { lng: 9.17, lat: 48.77 } }],
          // Porsche Asia Pacific
          [
            "AP",
            {
              zoomLevel: 2.8249545162684493,
              position: { lat: -13.922173356240634, lng: 112.33365032659333 },
            },
          ],
          // Porsche Middle East
          [
            "PME",
            {
              zoomLevel: 5.508610930683351,
              position: { lng: 33.02621042006815, lat: 31.779026148627622 },
            },
          ],
          // Porsche Central Europe and Eastern Europe
          [
            "PCEE",
            {
              zoomLevel: 4.920473879135225,
              position: { lat: 51.52179030823882, lng: 21.822870994237235 },
            },
          ],
          // Porsche Latin America
          [
            "PLA",
            {
              zoomLevel: 2.3,
              position: { lat: -13.66097702089597, lng: -76.13942639665365 },
            },
          ],
        ]),
      },
      search: {
        stationsAround: {
          availableOnly: false,
          connectors: {
            isHidden: true,
          },
        },
      },
    },
  },
  map: {
    style: {
      light: lightMapStyle,
    },
    defaultZoom: 12,
  },
  features: {
    station: {
      sortOrder: [
        "chargeAnimation",
        "evses",
        "openingHours",
        "amenities",
        "information",
        "identifiers",
      ],
      chargeStats: {
        isHidden: true,
      },
      chargeAnimation: {
        color: "--background-color-accent",
      },
      warnings: {
        isHidden: false,
        accessType: {
          isHidden: false,
        },
      },
      evses: {
        connectors: {
          price: {
            isHidden: true,
          },
          availability: {
            isHidden: true,
          },
          unknown: {
            isHidden: true,
          },
          indicators: {
            isHidden: true,
          },
        },
      },
      information: {
        streetView: {
          isHidden: true,
        },
      },
      pricing: {
        isHidden: true,
      },
      historicalAvailability: {
        isHidden: true,
      },
    },
    routing: {
      vehicle: {
        batteryAtOrigin: {
          defaultValue: 100,
        },
        occupants: {
          isHidden: true,
        },
        climateControl: {
          isHidden: true,
        },
        additionalWeight: {
          isHidden: true,
        },
      },
      route: {
        isExpandedByDefault: false,
      },
    },
    routeOverview: {
      overview: {
        chargeCosts: {
          isHidden: true,
        },
        co2Avoided: {
          isHidden: true,
        },
      },
      location: {
        chargeStop: {
          timelineItemDetails: {
            pricing: {
              isHidden: true,
            },
          },
        },
      },
    },
    rangeSpider: {
      vehicle: {
        occupants: {
          isHidden: true,
        },
        climateControl: {
          isHidden: true,
        },
        additionalWeight: {
          isHidden: true,
        },
      },
    },
    rangeSpiderOverview: {
      legend: {
        isReversed: true,
      },
    },
    settings: {
      primary: {
        faq: {
          isHidden: true,
        },
        whatsNew: {
          isHidden: true,
        },
        contact: {
          isHidden: true,
        },
        legend: {
          markers: {
            availability: {
              isHidden: true,
            },
            power: {
              operatorSpecific: {
                isHidden: true,
              },
            },
          },
        },
      },
    },
  },
  filters: {
    preferences: {
      preferredOperator: {
        isHidden: true,
      },
      operatorIds: [
        {
          icon: IconName.MAP_PORSCHE_DESTINATION,
          id: "porscheDestinationCharging",
          defaultValue: false,
          options: destinationOperators,
        },
        {
          icon: IconName.MAP_PORSCHE_LOUNGE,
          id: "porscheChargingLounges",
          defaultValue: false,
          options: loungeOperators,
        },
      ],
    },
    status: {
      isHidden: true,
    },
    connectors: {
      CHADEMO: {
        isHidden: true,
        defaultValue: false,
      },
    },
  },
};

export default config;
